import { getScaledPaperMarginLimits } from '@domainUtils'
import type { PlanTablePosition2D } from 'src/domain/planTables/positionAnchor'

/** Distance (paper cm) of an auto-placed table from the plan print area corner. */
const AUTO_PLACEMENT_GAP_CM = 1

/** Horizontal distance (paper cm) between auto-placed tables stacked side by side. */
const AUTO_PLACEMENT_OFFSET_CM = 10

/** Plan scale assumed when there are no plan settings. */
const FALLBACK_SCALE_DENOMINATOR = 100

const MAX_AUTO_PLACEMENT_SLOTS = 100

interface AutoPlanTablePositionInput {
  planSettings: PdfPlanSettings | undefined
  modelBounds: { xMax: number; yMax: number }
  existingPositions: PlanTablePosition2D[]
  z: number
}

/**
 * Position for a table anchored `top-left`: slots of a fixed width are lined up leftwards from the
 * top-right corner of the plan print area (or right of the model if there are no plan settings),
 * and the top-left corner of the first slot no existing table is pinned to is returned. Table sizes
 * are deliberately ignored.
 */
export const computeAutoPlanTablePosition = ({
  planSettings,
  modelBounds,
  existingPositions,
  z,
}: AutoPlanTablePositionInput): { x: number; y: number; z: number } => {
  const scaleDenominator = planSettings?.scale ?? FALLBACK_SCALE_DENOMINATOR
  const gap = (AUTO_PLACEMENT_GAP_CM / 100) * scaleDenominator
  const offset = (AUTO_PLACEMENT_OFFSET_CM / 100) * scaleDenominator

  let baseX: number
  let baseY: number
  if (planSettings) {
    const { xlim, ylim } = getScaledPaperMarginLimits(planSettings)
    baseX = xlim[1] - gap
    baseY = ylim[1] - gap
  } else {
    baseX = modelBounds.xMax + gap + offset
    baseY = modelBounds.yMax
  }

  const slotX = (slot: number) => baseX - (slot + 1) * offset
  const isSlotTaken = (x: number) =>
    existingPositions.some(position => Math.hypot(position.x - x, position.y - baseY) < offset / 2)

  let slot = 0
  while (slot < MAX_AUTO_PLACEMENT_SLOTS && isSlotTaken(slotX(slot))) slot += 1

  return { x: slotX(slot), y: baseY, z }
}
